/**
 * Appointments — admin view for date/meeting requests captured from any chat channel
 * (WhatsApp / Instagram / website chatbot, incl. anonymous), external API integrations,
 * and manual entries. Admin can filter, change status, reschedule, add, and delete.
 */

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Add,
  Api,
  CalendarMonth,
  Delete,
  Event,
  EventAvailable,
  EventBusy,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Language,
  Link as LinkIcon,
  Person,
  Schedule,
  TaskAlt,
} from '@mui/icons-material';
import InstagramLogo from '@/components/common/InstagramLogo';
import React, { Fragment, useEffect, useState } from 'react';

import apiService from '@/services/api.service';
import adminApi from '@/services/admin.api';
import EmptyState from '@/components/ui/EmptyState';
import PageHeader from '@/components/ui/PageHeader';
import StatCard from '@/components/ui/StatCard';
import StatusChip from '@/components/ui/StatusChip';
import SourceChip from '@/components/ui/SourceChip';
import {
  field,
  resolveUpdate,
  Updater,
  useUrlListState,
} from '@/hooks/admin/useUrlListState';

type Status = 'requested' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
type Source = 'whatsapp' | 'instagram' | 'webchat' | 'manual' | 'api';

interface AppointmentCustomAttribute {
  key: string;
  value: string | number | boolean;
}

interface Appointment {
  _id: string;
  source: Source;
  /** Drill-down for `source` — e.g. the external client's name for source==='api'. */
  sourceDetail?: string;
  customer?: { name?: string; phone?: string; email?: string; company?: string };
  requestedAt?: string;
  requestedText?: string;
  /** IANA name (e.g. Asia/Kolkata) requestedAt/requestedText should be read in. */
  timezone?: string;
  /** Short headline for what this appointment is about — distinct from `appointmentType`
   *  (a fixed category) and `notes` (longer freeform commentary). */
  title?: string;
  appointmentType?: string;
  meetingLink?: string;
  /** Caller's own booking reference (free text) — distinct from an external client's
   *  attribution-only externalAppointmentId, which is never surfaced here. */
  reference?: string;
  notes?: string;
  tags?: string[];
  customAttributes?: AppointmentCustomAttribute[];
  status: Status;
  leadId?: string;
  assignedTo?: string;
  createdAt: string;
}
interface TeamMember {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}
interface Stats {
  total: number;
  requested: number;
  confirmed: number;
  upcoming: number;
}

const STATUS_META: Record<Status, { label: string; icon: React.ReactElement }> = {
  requested: { label: 'Requested', icon: <Schedule sx={{ fontSize: 14 }} /> },
  confirmed: { label: 'Confirmed', icon: <EventAvailable sx={{ fontSize: 14 }} /> },
  completed: { label: 'Completed', icon: <TaskAlt sx={{ fontSize: 14 }} /> },
  cancelled: { label: 'Cancelled', icon: <EventBusy sx={{ fontSize: 14 }} /> },
  no_show: { label: 'No-show', icon: <EventBusy sx={{ fontSize: 14 }} /> },
};
const STATUSES: Status[] = ['requested', 'confirmed', 'completed', 'cancelled', 'no_show'];
const PAGE_SIZE = 20;
const BRAND = '#4f46e5';

function customerLabel(a: Appointment): string {
  const c = a.customer || {};
  return c.name || c.phone || c.email || 'Anonymous';
}
function customerInitial(a: Appointment): string {
  const c = a.customer || {};
  return (c.name?.[0] || c.phone?.[0] || c.email?.[0] || '?').toUpperCase();
}
function fmt(d?: string): string {
  if (!d) return '—';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '—' : date.toLocaleString();
}
function sourceChip(s: Source, sourceDetail?: string) {
  if (s === 'whatsapp') return <SourceChip source="whatsapp" />;
  if (s === 'webchat')
    return (
      <Chip
        size="small"
        icon={<Language sx={{ fontSize: 14 }} />}
        label="Web chat"
        variant="outlined"
      />
    );
  if (s === 'instagram')
    return (
      <Chip
        size="small"
        icon={<InstagramLogo size={14} sx={{ ml: 0.75 }} />}
        label="Instagram"
        variant="outlined"
      />
    );
  if (s === 'api') {
    return (
      <Tooltip
        title={sourceDetail ? `Via ${sourceDetail}` : ''}
        disableHoverListener={!sourceDetail}
      >
        <Chip
          size="small"
          icon={<Api sx={{ fontSize: 14 }} />}
          label={sourceDetail || 'API'}
          variant="outlined"
        />
      </Tooltip>
    );
  }
  return (
    <Chip size="small" icon={<Person sx={{ fontSize: 14 }} />} label="Manual" variant="outlined" />
  );
}

/** Status + page in the URL, so returning from a booking keeps the view. */
const APPOINTMENTS_URL_SPEC = {
  status: field.enumOf(['', 'requested', 'confirmed', 'cancelled', 'completed', 'no_show'] as const, ''),
  page: field.pageIndex(),
  /** Deep links from Web Captures / Chat Logs: only this lead's / this chat's appointments. */
  lead: field.str(),
  conversation: field.str(),
  /** Deep link from Web Captures: appointments booked in this website-chat session. */
  session: field.str(),
  /** Deep link from Chat Logs: only this customer's appointments (exact phone). */
  phone: field.str(),
};

const Appointments: React.FC = () => {
  const [rows, setRows] = useState<Appointment[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { state: query, patch: patchQuery } = useUrlListState(APPOINTMENTS_URL_SPEC);
  const statusFilter = query.status as Status | '';
  const leadFilter = query.lead;
  const conversationFilter = query.conversation;
  const phoneFilter = query.phone;
  const sessionFilter = query.session;
  const setStatusFilter = (value: Status | '') => patchQuery({ status: value });
  const page = query.page; // 0-indexed for TablePagination
  const setPage = (update: Updater<number>) =>
    patchQuery({ page: resolveUpdate(update, query.page) });
  const [total, setTotal] = useState(0);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    requestedAt: '',
    appointmentType: '',
    notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (statusFilter) params.set('status', statusFilter);
      if (leadFilter) params.set('leadId', leadFilter);
      if (conversationFilter) params.set('conversationId', conversationFilter);
      if (phoneFilter) params.set('phone', phoneFilter);
      if (sessionFilter) params.set('webChatSessionId', sessionFilter);
      const [listRes, statsRes] = await Promise.all([
        apiService.get(`/admin/appointments?${params.toString()}`),
        apiService.get('/admin/appointments/stats'),
      ]);
      const items: Appointment[] = listRes?.items || [];
      setStats(statsRes?.stats || null);
      setError(null);
      // Deleting the last row of the last page leaves it empty — step back one
      // page instead of showing a blank table; the effect refetches for it.
      if (items.length === 0 && page > 0) {
        setPage((p) => Math.max(0, p - 1));
        return;
      }
      setRows(items);
      setTotal(listRes?.total || 0);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  /** Lightweight — just the 4 counters, no full-page loading state. Used after a mutation
   *  that changes a status/count (status change, delete) so the stat tiles stay accurate
   *  without re-fetching (and re-flashing a spinner over) the whole table. */
  const refreshStats = async () => {
    try {
      const statsRes = await apiService.get('/admin/appointments/stats');
      setStats(statsRes?.stats || null);
    } catch {
      /* best-effort — stale counts for a moment beat a failed mutation */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, leadFilter, conversationFilter, phoneFilter, sessionFilter]);

  useEffect(() => {
    // Fetched once — same team-member list every "Assign to" select shares, no per-row
    // fetch. Mirrors Leads.tsx's own fetchTeamMembers call exactly.
    adminApi
      .getTeamDirectory({ suppressPermissionLock: true })
      .then((res: any) => setTeamMembers(res || []))
      .catch((e: any) => console.error('Error fetching team:', e));
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Every mutation below applies its own local state update FIRST (optimistic), then
  // confirms with the server — never a full `load()` for a single-row change, since that
  // flips `loading` and blanks the entire table behind a spinner for what should be an
  // instant inline edit. Counts (stat tiles) refresh separately and quietly via
  // refreshStats(). A full `load()` only ever runs on mount, filter/page change, or when
  // a page genuinely needs refilling (last row on a page removed).

  const changeStatus = async (id: string, status: Status) => {
    const prev = rows;
    setRows((r) => r.map((x) => (x._id === id ? { ...x, status } : x)));
    try {
      await apiService.patch(`/admin/appointments/${id}`, { status });
      refreshStats();
    } catch {
      setRows(prev); // revert on failure
    }
  };

  const changeAssignee = async (id: string, assignedTo: string) => {
    const prev = rows;
    setRows((r) =>
      r.map((x) => {
        if (x._id !== id) return x;
        const { assignedTo: _drop, ...rest } = x;
        return assignedTo ? { ...rest, assignedTo } : rest;
      })
    );
    try {
      await apiService.patch(`/admin/appointments/${id}`, { assignedTo: assignedTo || null });
    } catch {
      setRows(prev); // revert on failure
    }
  };

  const remove = async (id: string) => {
    const prev = rows;
    const wasLastOnPage = rows.length === 1 && page > 0;
    setRows((r) => r.filter((x) => x._id !== id));
    try {
      await apiService.delete(`/admin/appointments/${id}`);
      if (wasLastOnPage) {
        // Stepping back a page refetches (via the effect below) — that fetch already
        // covers the stats refresh, so no separate call here.
        setPage((p) => Math.max(0, p - 1));
      } else {
        setTotal((t) => Math.max(0, t - 1));
        refreshStats();
      }
    } catch {
      setRows(prev);
    }
  };

  const submitAdd = async () => {
    setSaving(true);
    try {
      const res = await apiService.post('/admin/appointments', {
        customer: { name: form.name, phone: form.phone, email: form.email },
        requestedAt: form.requestedAt ? new Date(form.requestedAt).toISOString() : undefined,
        appointmentType: form.appointmentType,
        notes: form.notes,
      });
      const created: Appointment | undefined = res?.appointment;
      setAddOpen(false);
      setForm({ name: '', phone: '', email: '', requestedAt: '', appointmentType: '', notes: '' });
      if (created) {
        setTotal((t) => t + 1);
        // Only splice it into view when it actually belongs on what's currently shown
        // (first page, and no filter or a matching one) — otherwise leave the list as-is
        // rather than surprising the admin by jumping them to a different page/filter.
        if (page === 0 && (!statusFilter || statusFilter === created.status)) {
          setRows((r) => [created, ...r].slice(0, PAGE_SIZE));
        }
        refreshStats();
      } else {
        load();
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to add appointment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Appointments"
        subtitle="Date & meeting requests captured from WhatsApp, the website chatbot (including anonymous visitors), external integrations, and manual entries."
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddOpen(true)}>
            Add appointment
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Total"
            value={stats?.total ?? 0}
            icon={<Event />}
            color={BRAND}
            loading={!stats && loading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Requested"
            value={stats?.requested ?? 0}
            icon={<Schedule />}
            color="#f59e0b"
            loading={!stats && loading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Confirmed"
            value={stats?.confirmed ?? 0}
            icon={<EventAvailable />}
            color="#1d4ed8"
            loading={!stats && loading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Upcoming"
            value={stats?.upcoming ?? 0}
            icon={<CalendarMonth />}
            color="#0891b2"
            loading={!stats && loading}
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Filter
            </Typography>
            <Select
              size="small"
              value={statusFilter}
              displayEmpty
              onChange={(e) => {
                setStatusFilter(e.target.value as Status | '');
                setPage(0);
              }}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_META[s].label}
                </MenuItem>
              ))}
            </Select>
            {(leadFilter || conversationFilter || phoneFilter || sessionFilter) && (
              <Chip
                size="small"
                color="primary"
                label={
                  leadFilter
                    ? 'Only this lead’s appointments'
                    : phoneFilter
                      ? `Only ${phoneFilter}`
                      : 'Only this chat’s appointments'
                }
                onDelete={() => {
                  patchQuery({ lead: '', conversation: '', phone: '', session: '', page: 0 });
                }}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Event />}
            title="No appointments yet"
            description="Requests from your chatbots will appear here automatically, or add one manually."
            action={{
              label: 'Add appointment',
              startIcon: <Add />,
              onClick: () => setAddOpen(true),
            }}
          />
        </Card>
      ) : (
        <>
          <TableContainer component={Card} sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Customer</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Requested for</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned to</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((a) => {
                  const isExpanded = expandedIds.has(a._id);
                  return (
                    <Fragment key={a._id}>
                      <TableRow hover>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => toggleExpand(a._id)}
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUp sx={{ fontSize: 18 }} />
                            ) : (
                              <KeyboardArrowDown sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: alpha(BRAND, 0.1),
                                color: BRAND,
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                              }}
                            >
                              {customerInitial(a)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {customerLabel(a)}
                              </Typography>
                              {(a.customer?.phone || a.customer?.email) && a.customer?.name && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  component="div"
                                >
                                  {a.customer?.phone || a.customer?.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {a.title ? (
                            <Tooltip title={a.notes || ''} disableHoverListener={!a.notes}>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {a.title}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {a.requestedAt ? (
                            <Typography variant="body2">{fmt(a.requestedAt)}</Typography>
                          ) : (
                            <Tooltip title="No specific time parsed">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic' }}
                              >
                                {a.requestedText || '—'}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {a.appointmentType || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {a.tags && a.tags.length > 0 ? (
                            <Box
                              sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 160 }}
                            >
                              {a.tags.slice(0, 2).map((tag) => (
                                <Chip key={tag} label={tag} size="small" />
                              ))}
                              {a.tags.length > 2 && (
                                <Chip
                                  label={`+${a.tags.length - 2}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{sourceChip(a.source, a.sourceDetail)}</TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            variant="standard"
                            disableUnderline
                            value={a.status}
                            onChange={(e) => changeStatus(a._id, e.target.value as Status)}
                            renderValue={(v) => {
                              const meta = STATUS_META[v as Status];
                              return (
                                <StatusChip
                                  status={v as string}
                                  label={meta.label}
                                  icon={meta.icon}
                                />
                              );
                            }}
                            sx={{ '& .MuiSelect-select': { py: 0, display: 'flex' } }}
                          >
                            {STATUSES.map((s) => (
                              <MenuItem key={s} value={s}>
                                {STATUS_META[s].label}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            variant="standard"
                            disableUnderline
                            displayEmpty
                            value={a.assignedTo || ''}
                            onChange={(e) => changeAssignee(a._id, e.target.value)}
                            sx={{ minWidth: 130, '& .MuiSelect-select': { py: 0 } }}
                          >
                            <MenuItem value="">
                              <Typography variant="body2" color="text.disabled">
                                Unassigned
                              </Typography>
                            </MenuItem>
                            {teamMembers.map((m) => (
                              <MenuItem key={m._id} value={m._id}>
                                {m.firstName} {m.lastName}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => remove(a._id)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'error.main', bgcolor: alpha('#ef4444', 0.08) },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 2 }}>
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                                  <Stack spacing={1.25}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">
                                        Created
                                      </Typography>
                                      <Typography variant="body2">{fmt(a.createdAt)}</Typography>
                                    </Box>
                                    {a.customer?.company && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          Company
                                        </Typography>
                                        <Typography variant="body2">
                                          {a.customer.company}
                                        </Typography>
                                      </Box>
                                    )}
                                    {a.meetingLink && (
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          Meeting link
                                        </Typography>
                                        <MuiLink
                                          href={a.meetingLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            fontSize: '0.875rem',
                                          }}
                                        >
                                          <LinkIcon sx={{ fontSize: 14 }} />
                                          Join meeting
                                        </MuiLink>
                                      </Box>
                                    )}
                                    {a.reference && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          Reference
                                        </Typography>
                                        <Typography variant="body2">{a.reference}</Typography>
                                      </Box>
                                    )}
                                    {a.timezone && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          Timezone
                                        </Typography>
                                        <Typography variant="body2">{a.timezone}</Typography>
                                      </Box>
                                    )}
                                    {a.sourceDetail && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          Source detail
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ overflowWrap: 'break-word' }}
                                        >
                                          {a.sourceDetail}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Stack>
                                </Grid>
                                <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                                  <Stack spacing={1.25}>
                                    {a.notes && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          Notes
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ overflowWrap: 'break-word' }}
                                        >
                                          {a.notes}
                                        </Typography>
                                      </Box>
                                    )}
                                    {a.customAttributes && a.customAttributes.length > 0 && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          Custom fields
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.75,
                                            mt: 0.5,
                                            maxWidth: '100%',
                                          }}
                                        >
                                          {a.customAttributes.map((attr) => (
                                            <Tooltip
                                              key={attr.key}
                                              title={`${attr.key}: ${attr.value}`}
                                            >
                                              <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`${attr.key}: ${attr.value}`}
                                                sx={{
                                                  maxWidth: 220,
                                                  '& .MuiChip-label': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                  },
                                                }}
                                              />
                                            </Tooltip>
                                          ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          )}
        </>
      )}

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add appointment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Customer name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                fullWidth
              />
            </Stack>
            <TextField
              label="Date & time"
              type="datetime-local"
              value={form.requestedAt}
              onChange={(e) => setForm((f) => ({ ...f, requestedAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Type"
              placeholder="e.g. consultation"
              value={form.appointmentType}
              onChange={(e) => setForm((f) => ({ ...f, appointmentType: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitAdd} disabled={saving}>
            {saving ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Appointments;
